// @flow

import AppDispatcher from 'PMT/flux/AppDispatcher';

import ApiRestaurantMenu from 'PMT/api/ApiRestaurantMenu';

// -- constants
import ActionTypes from 'constants/ActionTypes';
import RoutingEnum from 'constants/RoutingEnum';

import ViewUtils from 'PMT/utils/ViewUtils';

// -- stores
import RouteStore from 'stores/RouteStore';
import SessionStore from 'stores/SessionStore';

// -- entities
import ApiError from 'PMT/entities/ApiError';
import {
  RestaurantMenu,
  Menu,
  ProductOption,
  Product,
  ProductCategory,
  UpsellingCategory,
} from 'PMT/entities/menu';

export default class RestaurantMenuAction {
  //
  // RestaurantMenu - Load
  //

  static loadRestaurantMenu() {
    AppDispatcher.handleServerAction({
      type: ActionTypes.GET_RESTAURANT_MENU,
    });

    ApiRestaurantMenu.getLocalMenu(
      SessionStore.getRestaurantId(),
      RestaurantMenuAction.receiveRestaurantMenu,
      RestaurantMenuAction.receiveRestaurantMenuError
    );
  }

  static receiveRestaurantMenu(restaurantMenu: RestaurantMenu) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.GET_RESTAURANT_MENU_RESPONSE_SUCCESS,
      restaurantMenu,
    });
  }

  static receiveRestaurantMenuError(apiError: ApiError) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.GET_RESTAURANT_MENU_RESPONSE_FAILURE,
      apiError,
    });
  }

  //
  // RestaurantMenu - Load force refrsh
  //

  static loadRestaurantMenuWithSynchronization() {
    // To test error handling
    // const apiError = new ApiError();
    // RestaurantMenuAction.receiveRestaurantMenuError(apiError);
    // return;

    ApiRestaurantMenu.getMenuWithSynchronization(
      SessionStore.getRestaurantId(),
      RestaurantMenuAction.receiveRestaurantMenuWithSynchronization,
      RestaurantMenuAction.receiveRestaurantMenuError
    );
  }

  static receiveRestaurantMenuWithSynchronization(
    restaurantMenu: RestaurantMenu
  ) {
    // updating the data on our restaurant menu pages is too complicated, we reload the whole page
    location.reload();
    // AppDispatcher.handleViewAction({
    //   type:
    //     ActionTypes.GET_RESTAURANT_MENU_WITH_SYNCHRONIZATION_RESPONSE_SUCCESS,
    //   restaurantMenu,
    // });
  }

  static receiveRestaurantMenuWithSynchronizationError(apiError: ApiError) {
    AppDispatcher.handleViewAction({
      type:
        ActionTypes.GET_RESTAURANT_MENU_WITH_SYNCHRONIZATION_RESPONSE_FAILURE,
      apiError,
    });
  }

  //
  //  RestaurantMenu - Update
  //

  static saveRestaurantMenu(restaurantMenu: RestaurantMenu) {
    ApiRestaurantMenu.putMenu(
      SessionStore.getRestaurantId(),
      restaurantMenu,
      RestaurantMenuAction.receiveUpdatedRestaurantMenu,
      RestaurantMenuAction.receiveUpdatedRestaurantMenuError
    );
  }

  static receiveUpdatedRestaurantMenu(restaurantMenu: RestaurantMenu) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.PUT_RESTAURANT_MENU_RESPONSE_SUCCESS,
      restaurantMenu,
    });
  }

  static receiveUpdatedRestaurantMenuError(apiError: ApiError) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.PUT_RESTAURANT_MENU_RESPONSE_FAILURE,
      apiError,
    });
  }

  //
  // PRODUCT - UPDATE
  //

  static saveProduct(product: Product, image) {
    ApiRestaurantMenu.putMenuProduct(
      SessionStore.getRestaurantId(),
      product,
      image,
      RestaurantMenuAction.receiveUpdatedRestaurantMenuProduct,
      RestaurantMenuAction.receiveUpdatedRestaurantMenuProductError
    );
  }

  static receiveUpdatedRestaurantMenuProduct(product: Product) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.PUT_RESTAURANT_MENU_PRODUCT_RESPONSE_SUCCESS,
      product,
    });
  }

  static receiveUpdatedRestaurantMenuProductError(apiError: ApiError) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.PUT_RESTAURANT_MENU_PRODUCT_RESPONSE_FAILURE,
      apiError,
    });
  }

  static deleteProductImage(product: Product) {
    ApiRestaurantMenu.deleteMenuProductImage(
      SessionStore.getRestaurantId(),
      product,
      RestaurantMenuAction.receiveDeletedProductImage,
      RestaurantMenuAction.receiveDeletedProductImageError
    );
  }

  static receiveDeletedProductImage(product: Product) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.DELETE_RESTAURANT_MENU_PRODUCT_IMAGE_RESPONSE_SUCCESS,
      product,
    });
  }

  static receiveDeletedProductImageError(apiError: ApiError) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.DELETE_RESTAURANT_MENU_PRODUCT_IMAGE_RESPONSE_FAILURE,
      apiError,
    });
  }

  //
  // PRODUCT - CREATE
  //

  static createProduct(product: Product, image) {
    ApiRestaurantMenu.postMenuProduct(
      SessionStore.getRestaurantId(),
      product,
      image,
      RestaurantMenuAction.receiveCreatedRestaurantMenuProduct,
      RestaurantMenuAction.receiveCreatedRestaurantMenuProductError
    );
  }

  static receiveCreatedRestaurantMenuProduct(product: Product) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.POST_RESTAURANT_MENU_PRODUCT_RESPONSE_SUCCESS,
      product,
    });
  }

  static receiveCreatedRestaurantMenuProductError(apiError: ApiError) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.POST_RESTAURANT_MENU_PRODUCT_RESPONSE_FAILURE,
      apiError,
    });
  }

  //
  // PRODUCT - DELETE
  //

  static deleteProduct(product: Product) {
    ApiRestaurantMenu.deleteMenuProduct(
      SessionStore.getRestaurantId(),
      product,
      RestaurantMenuAction.receiveDeletedRestaurantMenuProduct,
      RestaurantMenuAction.receiveDeletedRestaurantMenuProductError
    );
  }

  static receiveDeletedRestaurantMenuProduct(product: Product) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.DELETE_RESTAURANT_MENU_PRODUCT_RESPONSE_SUCCESS,
      product,
    });
  }

  static receiveDeletedRestaurantMenuProductError(apiError: ApiError) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.DELETE_RESTAURANT_MENU_PRODUCT_RESPONSE_FAILURE,
      apiError,
    });
  }

  //
  // MENU - UPDATE
  //

  static saveMenu(menu: Menu, image) {
    ApiRestaurantMenu.putMenuMenu(
      SessionStore.getRestaurantId(),
      menu,
      image,
      RestaurantMenuAction.receiveUpdatedRestaurantMenuMenu,
      RestaurantMenuAction.receiveUpdatedRestaurantMenuMenuError
    );
  }

  static receiveUpdatedRestaurantMenuMenu(menu: Menu) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.PUT_RESTAURANT_MENU_MENU_RESPONSE_SUCCESS,
      menu,
    });
  }

  static receiveUpdatedRestaurantMenuMenuError(apiError: ApiError) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.PUT_RESTAURANT_MENU_MENU_RESPONSE_FAILURE,
      apiError,
    });
  }

  static deleteMenuImage(menu: Menu) {
    ApiRestaurantMenu.deleteMenuMenuImage(
      SessionStore.getRestaurantId(),
      menu,
      RestaurantMenuAction.receiveDeletedRestaurantMenuMenuImage,
      RestaurantMenuAction.receiveDeletedRestaurantMenuMenuImageError
    );
  }

  static receiveDeletedRestaurantMenuMenuImage(menu: Menu) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.DELETE_RESTAURANT_MENU_MENU_IMAGE_RESPONSE_SUCCESS,
      menu,
    });
  }

  static receiveDeletedRestaurantMenuMenuImageError(apiError: ApiError) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.DELETE_RESTAURANT_MENU_MENU_IMAGE_RESPONSE_FAILURE,
      apiError,
    });
  }

  //
  // MENU - CREATE
  //

  static createMenu(menu: Menu, image) {
    ApiRestaurantMenu.postMenuMenu(
      SessionStore.getRestaurantId(),
      menu,
      image,
      RestaurantMenuAction.receiveCreatedRestaurantMenuMenu,
      RestaurantMenuAction.receiveCreatedRestaurantMenuMenuError
    );
  }

  static receiveCreatedRestaurantMenuMenu(menu: Menu) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.POST_RESTAURANT_MENU_MENU_RESPONSE_SUCCESS,
      menu,
    });
  }

  static receiveCreatedRestaurantMenuMenuError(apiError: ApiError) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.POST_RESTAURANT_MENU_MENU_RESPONSE_FAILURE,
      apiError,
    });
  }

  //
  // MENU - DELETE
  //

  static deleteMenu(menu: Menu) {
    ApiRestaurantMenu.deleteMenuMenu(
      SessionStore.getRestaurantId(),
      menu,
      RestaurantMenuAction.receiveDeletedRestaurantMenuMenu,
      RestaurantMenuAction.receiveDeletedRestaurantMenuMenuError
    );
  }

  static receiveDeletedRestaurantMenuMenu(menu: Menu) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.DELETE_RESTAURANT_MENU_MENU_RESPONSE_SUCCESS,
      menu,
    });
  }

  static receiveDeletedRestaurantMenuMenuError(apiError: ApiError) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.DELETE_RESTAURANT_MENU_MENU_RESPONSE_FAILURE,
      apiError,
    });
  }

  //
  // MenuPart - UPDATE
  //

  static updateMenuPart(menu, menuPart) {
    ApiRestaurantMenu.updateMenuPart(
      SessionStore.getRestaurantId(),
      menu,
      menuPart,
      (updatedMenuPart: MenuPart) => {
        AppDispatcher.handleViewAction({
          type: ActionTypes.PUT_RESTAURANT_MENU_MENU_PART_SUCCESS,
          menuId: menu.id,
          menuPart: updatedMenuPart,
        });
      },
      apiError => {
        AppDispatcher.handleViewAction({
          type: ActionTypes.PUT_RESTAURANT_MENU_MENU_PART_FAILURE,
          apiError,
        });
      }
    );
  }

  //
  // MenuPartCategory - UPDATE
  //

  static updateMenuPartCategory(
    menu,
    menuPart: MenuPart,
    menuPartCategory: MenuPartCategory
  ) {
    ApiRestaurantMenu.updateMenuPartCategory(
      SessionStore.getRestaurantId(),
      menu,
      menuPart,
      menuPartCategory,
      (updatedMenuPartCategory: MenuPartCategory) => {
        AppDispatcher.handleViewAction({
          type: ActionTypes.PUT_RESTAURANT_MENU_MENU_PART_CATEGORY_SUCCESS,
          menuId: menu.id,
          menuPartId: menuPart.id,
          category: updatedMenuPartCategory,
        });
      },
      apiError => {
        AppDispatcher.handleViewAction({
          type: ActionTypes.PUT_RESTAURANT_MENU_MENU_PART_CATEGORY_FAILURE,
          apiError,
        });
      }
    );
  }

  static uploadMenuPartCategoryImage(
    menu,
    menuPart: MenuPart,
    menuPartCategory: MenuPartCategory,
    image
  ) {
    ApiRestaurantMenu.uploadMenuPartCategoryImage(
      SessionStore.getRestaurantId(),
      menu,
      menuPart,
      menuPartCategory,
      ViewUtils.dataUriToFile(image),
      (updatedMenuPartCategory: MenuPartCategory) => {
        AppDispatcher.handleViewAction({
          type: ActionTypes.PUT_RESTAURANT_MENU_MENU_PART_CATEGORY_SUCCESS,
          menuId: menu.id,
          menuPartId: menuPart.id,
          category: updatedMenuPartCategory,
        });
      },
      apiError => {
        AppDispatcher.handleViewAction({
          type: ActionTypes.PUT_RESTAURANT_MENU_MENU_PART_CATEGORY_FAILURE,
          apiError,
        });
      }
    );
  }

  static removeMenuPartCategoryImage(
    menu,
    menuPart: MenuPart,
    menuPartCategory: MenuPartCategory
  ) {
    ApiRestaurantMenu.removeMenuPartCategoryImage(
      SessionStore.getRestaurantId(),
      menu,
      menuPart,
      menuPartCategory,
      (updatedMenuPartCategory: MenuPartCategory) => {
        AppDispatcher.handleViewAction({
          type: ActionTypes.PUT_RESTAURANT_MENU_MENU_PART_CATEGORY_SUCCESS,
          menuId: menu.id,
          menuPartId: menuPart.id,
          category: updatedMenuPartCategory,
        });
      },
      apiError => {
        AppDispatcher.handleViewAction({
          type: ActionTypes.PUT_RESTAURANT_MENU_MENU_PART_CATEGORY_FAILURE,
          apiError,
        });
      }
    );
  }


  //
  // MenuPartCategory - DELETE
  //

  static deleteMenuPartCategory(
    menu,
    menuPart: MenuPart,
    menuPartCategory: MenuPartCategory
  ) {
    ApiRestaurantMenu.deleteMenuPartCategory(
      SessionStore.getRestaurantId(),
      menu,
      menuPart,
      menuPartCategory,
      () => {
        AppDispatcher.handleViewAction({
          type: ActionTypes.DELETE_RESTAURANT_MENU_MENU_PART_CATEGORY_SUCCESS,
          menuId: menu.id,
          menuPartId: menuPart.id,
          category: menuPartCategory,
        });
      },
      apiError => {
        AppDispatcher.handleViewAction({
          type: ActionTypes.DELETE_RESTAURANT_MENU_MENU_PART_CATEGORY_FAILURE,
          apiError,
        });
      }
    );
  }

  //
  // CATEGORY - UPDATE
  //

  static saveCategory(category: ProductCategory, image) {
    ApiRestaurantMenu.putMenuCategory(
      SessionStore.getRestaurantId(),
      category,
      image,
      RestaurantMenuAction.receiveUpdatedRestaurantMenuCategory,
      RestaurantMenuAction.receiveUpdatedRestaurantMenuCategoryError
    );
  }

  static receiveUpdatedRestaurantMenuCategory(category: ProductCategory) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.PUT_RESTAURANT_MENU_CATEGORY_RESPONSE_SUCCESS,
      category,
    });
  }

  static receiveUpdatedRestaurantMenuCategoryError(apiError: ApiError) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.PUT_RESTAURANT_MENU_CATEGORY_RESPONSE_FAILURE,
      apiError,
    });
  }

  static deleteCategoryImage(category: ProductCategory) {
    ApiRestaurantMenu.deleteMenuCategoryImage(
      SessionStore.getRestaurantId(),
      category,
      RestaurantMenuAction.receiveUpdatedRestaurantMenuCategoryImage,
      RestaurantMenuAction.receiveUpdatedRestaurantMenuCategoryImageError
    );
  }

  static receiveUpdatedRestaurantMenuCategoryImage(category: ProductCategory) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.DELETE_RESTAURANT_MENU_CATEGORY_IMAGE_RESPONSE_SUCCESS,
      category,
    });
  }

  static receiveUpdatedRestaurantMenuCategoryImageError(apiError: ApiError) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.DELETE_RESTAURANT_MENU_CATEGORY_IMAGE_RESPONSE_FAILURE,
      apiError,
    });
  }

  //
  // CATEGORY - CREATE
  //

  static createCategory(category: ProductCategory, image) {
    ApiRestaurantMenu.postMenuCategory(
      SessionStore.getRestaurantId(),
      category,
      image,
      RestaurantMenuAction.receiveCreatedRestaurantMenuCategory,
      RestaurantMenuAction.receiveCreatedRestaurantMenuCategoryError
    );
  }

  static receiveCreatedRestaurantMenuCategory(category: ProductCategory) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.POST_RESTAURANT_MENU_CATEGORY_RESPONSE_SUCCESS,
      category,
    });
  }

  static receiveCreatedRestaurantMenuCategoryError(apiError: ApiError) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.POST_RESTAURANT_MENU_CATEGORY_RESPONSE_FAILURE,
      apiError,
    });
  }

  //
  // CATEGORY - DELETE
  //

  static deleteCategory(category: ProductCategory, image) {
    ApiRestaurantMenu.deleteMenuCategory(
      SessionStore.getRestaurantId(),
      category,
      image,
      RestaurantMenuAction.receiveDeletedRestaurantMenuCategory,
      RestaurantMenuAction.receiveDeletedRestaurantMenuCategoryError
    );
  }

  static receiveDeletedRestaurantMenuCategory(category: ProductCategory) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.DELETE_RESTAURANT_MENU_CATEGORY_RESPONSE_SUCCESS,
      category,
    });
  }

  static receiveDeletedRestaurantMenuCategoryError(apiError: ApiError) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.DELETE_RESTAURANT_MENU_CATEGORY_RESPONSE_FAILURE,
      apiError,
    });
  }

  //
  // PRODUCT OPTION - UPDATE
  //

  static saveProductOption(productOption: ProductOption, image) {
    ApiRestaurantMenu.putMenuProductOption(
      SessionStore.getRestaurantId(),
      productOption,
      image,
      RestaurantMenuAction.receiveUpdatedRestaurantMenuProductOption,
      RestaurantMenuAction.receiveUpdatedRestaurantMenuProductOptionError
    );
  }

  static receiveUpdatedRestaurantMenuProductOption(
    productOption: ProductOption
  ) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.PUT_RESTAURANT_MENU_PRODUCT_OPTION_SUCCESS,
      productOption,
    });
  }

  static receiveUpdatedRestaurantMenuProductOptionError(apiError: ApiError) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.PUT_RESTAURANT_MENU_PRODUCT_OPTION_FAILURE,
      apiError,
    });
  }

  static deleteProductOptionImage(productOption: ProductOption) {
    ApiRestaurantMenu.deleteMenuProductOptionImage(
      SessionStore.getRestaurantId(),
      productOption,
      RestaurantMenuAction.receiveUpdatedRestaurantMenuProductOptionImage,
      RestaurantMenuAction.receiveUpdatedRestaurantMenuProductOptionImageError
    );
  }

  static receiveUpdatedRestaurantMenuProductOptionImage(
    productOption: ProductOption
  ) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.DELETE_RESTAURANT_MENU_PRODUCT_OPTION_IMAGE_SUCCESS,
      productOption,
    });
  }

  static receiveUpdatedRestaurantMenuProductOptionImageError(
    apiError: ApiError
  ) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.DELETE_RESTAURANT_MENU_PRODUCT_OPTION_IMAGE_FAILURE,
      apiError,
    });
  }

  //
  // PRODUCT OPTION - CREATE
  //

  static createProductOption(productOption: ProductOption, image) {
    ApiRestaurantMenu.postMenuProductOption(
      SessionStore.getRestaurantId(),
      productOption,
      image,
      RestaurantMenuAction.receiveCreatedRestaurantMenuProductOption,
      RestaurantMenuAction.receiveCreatedRestaurantMenuProductOptionError
    );
  }

  static receiveCreatedRestaurantMenuProductOption(
    productOption: ProductOption
  ) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.POST_RESTAURANT_MENU_PRODUCT_OPTION_SUCCESS,
      productOption,
    });
  }

  static receiveCreatedRestaurantMenuProductOptionError(apiError: ApiError) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.POST_RESTAURANT_MENU_PRODUCT_OPTION_FAILURE,
      apiError,
    });
  }

  //
  // PRODUCT OPTION - DELETE
  //

  static deleteProductOption(productOption: ProductOption) {
    ApiRestaurantMenu.deleteMenuProductOption(
      SessionStore.getRestaurantId(),
      productOption,
      RestaurantMenuAction.receiveDeletedRestaurantMenuProductOption,
      RestaurantMenuAction.receiveDeletedRestaurantMenuProductOptionError
    );
  }

  static receiveDeletedRestaurantMenuProductOption(
    productOption: ProductOption
  ) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.DELETE_RESTAURANT_MENU_PRODUCT_OPTION_SUCCESS,
      productOption,
    });
  }

  static receiveDeletedRestaurantMenuProductOptionError(apiError: ApiError) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.DELETE_RESTAURANT_MENU_PRODUCT_OPTION_FAILURE,
      apiError,
    });
  }

  //
  // CATEGORY - UPDATE
  //

  static saveUpsellingCategory(upsellingCategory: UpsellingCategory, image) {
    ApiRestaurantMenu.putMenuUpsellingCategory(
      SessionStore.getRestaurantId(),
      upsellingCategory,
      image,
      RestaurantMenuAction.receiveUpdatedRestaurantMenuUpsellingCategory,
      RestaurantMenuAction.receiveUpdatedRestaurantMenuUpsellingCategoryError
    );
  }

  static receiveUpdatedRestaurantMenuUpsellingCategory(
    upsellingCategory: UpsellingCategory
  ) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.PUT_RESTAURANT_MENU_UPSELLING_CATEGORY_RESPONSE_SUCCESS,
      upsellingCategory,
    });
  }

  static receiveUpdatedRestaurantMenuUpsellingCategoryError(
    apiError: ApiError
  ) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.PUT_RESTAURANT_MENU_UPSELLING_CATEGORY_RESPONSE_FAILURE,
      apiError,
    });
  }

  //
  // CATEGORY - CREATE
  //

  static createUpsellingCategory(upsellingCategory: UpsellingCategory, image) {
    ApiRestaurantMenu.postMenuUpsellingCategory(
      SessionStore.getRestaurantId(),
      upsellingCategory,
      image,
      RestaurantMenuAction.receiveCreatedRestaurantMenuUpsellingCategory,
      RestaurantMenuAction.receiveCreatedRestaurantMenuUpsellingCategoryError
    );
  }

  static receiveCreatedRestaurantMenuUpsellingCategory(
    upsellingCategory: UpsellingCategory
  ) {
    AppDispatcher.handleViewAction({
      type:
        ActionTypes.POST_RESTAURANT_MENU_UPSELLING_CATEGORY_RESPONSE_SUCCESS,
      upsellingCategory,
    });
  }

  static receiveCreatedRestaurantMenuUpsellingCategoryError(
    apiError: ApiError
  ) {
    AppDispatcher.handleViewAction({
      type:
        ActionTypes.POST_RESTAURANT_MENU_UPSELLING_CATEGORY_RESPONSE_FAILURE,
      apiError,
    });
  }

  //
  // CATEGORY - DELETE
  //

  static deleteUpsellingCategory(upsellingCategory: UpsellingCategory, image) {
    ApiRestaurantMenu.deleteMenuUpsellingCategory(
      SessionStore.getRestaurantId(),
      upsellingCategory,
      image,
      RestaurantMenuAction.receiveDeletedRestaurantMenuUpsellingCategory,
      RestaurantMenuAction.receiveDeletedRestaurantMenuUpsellingCategoryError
    );
  }

  static receiveDeletedRestaurantMenuUpsellingCategory(
    upsellingCategory: UpsellingCategory
  ) {
    AppDispatcher.handleViewAction({
      type:
        ActionTypes.DELETE_RESTAURANT_MENU_UPSELLING_CATEGORY_RESPONSE_SUCCESS,
      upsellingCategory,
    });
  }

  static receiveDeletedRestaurantMenuUpsellingCategoryError(
    apiError: ApiError
  ) {
    AppDispatcher.handleViewAction({
      type:
        ActionTypes.DELETE_RESTAURANT_MENU_UPSELLING_CATEGORY_RESPONSE_FAILURE,
      apiError,
    });
  }
}



// WEBPACK FOOTER //
// ./scripts/actions/RestaurantMenuAction.js